import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Card } from "antd";
import { toast } from "react-hot-toast";
import Footer from "../components/Footer";
import Header from "../components/Header";
import { useTranslation } from "react-i18next";
import StandardButton from "../components/StandardButton";
import {
  HTTP_STATUS_CLASSES,
  HTTP_STATUS_CODES,
} from "../data/httpStatusCodes";

const entryDomId = (code) => `http-status-${code}`;

const CATEGORY_STYLES = {
  "1xx": {
    badge: "bg-sky-100 text-sky-700 border-sky-200",
    accent: "border-sky-200",
  },
  "2xx": {
    badge: "bg-emerald-100 text-emerald-700 border-emerald-200",
    accent: "border-emerald-200",
  },
  "3xx": {
    badge: "bg-amber-100 text-amber-700 border-amber-200",
    accent: "border-amber-200",
  },
  "4xx": {
    badge: "bg-orange-100 text-orange-700 border-orange-200",
    accent: "border-orange-200",
  },
  "5xx": {
    badge: "bg-red-100 text-red-700 border-red-200",
    accent: "border-red-200",
  },
};

const HttpStatusLookup = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const linkedCode = useMemo(() => {
    const raw = searchParams.get("code");
    if (!raw) return null;
    const n = Number.parseInt(raw, 10);
    return HTTP_STATUS_CODES.some((e) => e.code === n) ? n : null;
  }, [searchParams]);

  const [search, setSearch] = useState(() =>
    linkedCode != null ? String(linkedCode) : "",
  );
  const [activeClass, setActiveClass] = useState("all");
  const [expanded, setExpanded] = useState(() =>
    linkedCode != null ? new Set([linkedCode]) : new Set(),
  );
  const searchInputRef = useRef(null);
  const didScrollToLinkedRef = useRef(false);

  const toggleExpanded = (code) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  };

  useEffect(() => {
    if (linkedCode == null || didScrollToLinkedRef.current) return;
    const node = document.getElementById(entryDomId(linkedCode));
    if (!node) return;
    didScrollToLinkedRef.current = true;
    node.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [linkedCode]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key !== "/" || e.metaKey || e.ctrlKey || e.altKey) return;
      const target = e.target;
      const tag = target?.tagName;
      if (
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        tag === "SELECT" ||
        target?.isContentEditable
      ) {
        return;
      }
      e.preventDefault();
      searchInputRef.current?.focus();
      searchInputRef.current?.select?.();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const handleSearchKeyDown = (e) => {
    if (e.key !== "Escape") return;
    if (!search) {
      e.target.blur();
      return;
    }
    e.preventDefault();
    setSearch("");
  };

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return HTTP_STATUS_CODES.filter((entry) => {
      if (activeClass !== "all" && entry.category !== activeClass) return false;
      if (!term) return true;
      const haystack = `${entry.code} ${entry.name} ${entry.summary} ${entry.details}`.toLowerCase();
      return haystack.includes(term);
    });
  }, [search, activeClass]);

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} ${t("copy_success")}`);
    } catch {
      toast.error(t("failed_to_copy"));
    }
  };

  const copyLink = (code) => {
    const url = new URL(window.location.href);
    url.search = "";
    url.hash = "";
    url.searchParams.set("code", String(code));
    return copy(url.toString(), t("http_status_copy_link_label"));
  };

  const filterButtons = [
    { key: "all", labelKey: "http_status_filter_all" },
    ...HTTP_STATUS_CLASSES.map((cls) => ({
      key: cls,
      labelKey: `http_status_filter_${cls}`,
    })),
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-indigo-50 to-white p-0 text-gray-800">
      <Header />
      <div className="max-w-5xl mx-auto p-4">
        <Card className="shadow-lg rounded-xl">
          <div className="p-4 flex flex-col gap-4">
            <h1 className="text-2xl font-bold text-indigo-700">
              {t("http_status_title")}
            </h1>
            <p className="text-gray-600">{t("http_status_intro")}</p>

            <div className="flex flex-col gap-1">
              <label
                htmlFor="http-status-search"
                className="text-sm font-medium text-gray-700"
              >
                {t("http_status_search_label")}
              </label>
              <input
                id="http-status-search"
                ref={searchInputRef}
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                placeholder={t("http_status_search_placeholder")}
                aria-label={t("http_status_search_label")}
                aria-keyshortcuts="/"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400 focus:outline-none"
              />
              <span className="text-xs text-gray-500">
                {t("http_status_shortcut_hint")}
              </span>
            </div>

            <div className="flex flex-wrap gap-2">
              {filterButtons.map((btn) => (
                <StandardButton
                  key={btn.key}
                  onClick={() => setActiveClass(btn.key)}
                  type={activeClass === btn.key ? "primary" : "secondary"}
                  variant={activeClass === btn.key ? undefined : "outlined"}
                  size="small"
                >
                  {t(btn.labelKey)}
                </StandardButton>
              ))}
            </div>

            <div className="text-xs text-gray-500">
              {t("http_status_count", { count: filtered.length })}
            </div>

            {filtered.length === 0 ? (
              <div className="bg-gray-50 border border-gray-200 text-gray-600 p-4 rounded-md text-sm">
                {t("http_status_no_match")}
              </div>
            ) : (
              <ul className="flex flex-col gap-2">
                {filtered.map((entry) => {
                  const style =
                    CATEGORY_STYLES[entry.category] || CATEGORY_STYLES["4xx"];
                  const isOpen = expanded.has(entry.code);
                  const isLinked = linkedCode === entry.code;
                  return (
                    <li
                      key={entry.code}
                      id={entryDomId(entry.code)}
                      className={`bg-white border ${style.accent} rounded-md p-3 ${
                        isLinked ? "ring-2 ring-indigo-400" : ""
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <span
                          className={`inline-flex items-center justify-center min-w-[3.25rem] px-2 py-1 text-sm font-mono font-bold rounded border ${style.badge}`}
                        >
                          {entry.code}
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-baseline gap-x-2">
                            <span className="font-semibold text-gray-900">
                              {entry.name}
                            </span>
                            <span className="text-xs uppercase tracking-wide text-gray-500">
                              {entry.category}
                            </span>
                          </div>
                          <p className="text-sm text-gray-700 mt-1">
                            {entry.summary}
                          </p>
                          {isOpen && (
                            <p className="text-sm text-gray-600 mt-2 leading-relaxed">
                              {entry.details}
                            </p>
                          )}
                        </div>
                        <div className="flex flex-col gap-1 shrink-0">
                          <StandardButton
                            onClick={() => toggleExpanded(entry.code)}
                            type="secondary"
                            variant="outlined"
                            size="small"
                          >
                            {isOpen
                              ? t("http_status_hide_details")
                              : t("http_status_show_details")}
                          </StandardButton>
                          <StandardButton
                            onClick={() =>
                              copy(
                                String(entry.code),
                                t("http_status_copy_code_label"),
                              )
                            }
                            type="secondary"
                            variant="outlined"
                            size="small"
                          >
                            {t("http_status_copy_code_button")}
                          </StandardButton>
                          <StandardButton
                            onClick={() =>
                              copy(
                                `${entry.code} ${entry.name}`,
                                t("http_status_copy_full_label"),
                              )
                            }
                            type="secondary"
                            variant="outlined"
                            size="small"
                          >
                            {t("http_status_copy_full_button")}
                          </StandardButton>
                          <StandardButton
                            onClick={() => copyLink(entry.code)}
                            type="secondary"
                            variant="outlined"
                            size="small"
                            aria-label={t("http_status_copy_link_aria", {
                              code: entry.code,
                            })}
                          >
                            {t("http_status_copy_link_button")}
                          </StandardButton>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}

            <div className="text-xs text-gray-500 leading-relaxed">
              <p>{t("http_status_note_canonical")}</p>
              <p>{t("http_status_note_extensions")}</p>
              <p>{t("http_status_note_privacy")}</p>
            </div>
          </div>
        </Card>
      </div>
      <Footer />
    </div>
  );
};

export default HttpStatusLookup;
